/**
 * The end of a Tide Run: "The tide got you!" (or, off the top of level 100,
 * "You outran the tide!"), what the run did, any records it beat, and a popup
 * for each colour it earned. Never failure language beyond that gentle line
 * (CLAUDE.md): the summary leads with what the player cleared.
 */
import Phaser from 'phaser';
import { drawVerticalGradient } from '../art/backdrop';
import { ensurePlaceholderTexture } from '../art/placeholders';
import { animalById, COLOR_SWATCHES } from '../data/animals';
import { audioOf } from '../systems/audioRegistry';
import { setDevState } from '../systems/devQuery';
import { formatClock, startTideRun } from '../systems/TideRun';
import type { CosmeticId, TideRunSummary } from '../systems/TideRun';
import type { SaveManager, TideRecordName, TideRunResult } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import { pressButton, styleButton } from './ui/menuButton';

export interface TideSummaryData {
  readonly summary: TideRunSummary;
  readonly result: TideRunResult;
}

const RECORD_WORDS: Readonly<Record<TideRecordName, string>> = {
  furthest: 'furthest level',
  most: 'most levels in one run',
  wpm: 'fastest run',
};

const TITLE_Y = IS_PORTRAIT ? 110 : 64;
const STATS_TOP = IS_PORTRAIT ? 210 : 136;
const STAT_GAP = IS_PORTRAIT ? 46 : 36;
/** How many of the run's clears are listed, newest last. */
const CLEARS_SHOWN = 4;

/** A run for `?scene=TideSummary`, so the layout can be reviewed. */
function sampleData(): TideSummaryData {
  return {
    summary: {
      outcome: 'caught',
      startLevel: 1,
      reachedLevel: 5,
      levelsCleared: 4,
      elapsedMs: 251000,
      wpm: 13.4,
      accuracy: 94.2,
      tideStarsEarned: 7,
      clears: [
        { level: 1, timeSec: 41.2, stars: 2, newBest: true },
        { level: 2, timeSec: 52.9, stars: 2, newBest: false },
        { level: 3, timeSec: 61.4, stars: 1, newBest: true },
        { level: 4, timeSec: 58.0, stars: 2, newBest: true },
      ],
    },
    result: {
      newRecords: ['furthest'],
      newCosmetics: ['swatch-ocean'],
      newUnlocks: [],
      tideStarsTotal: 12,
    },
  };
}

export class TideSummary extends Phaser.Scene {
  private shown: TideSummaryData = sampleData();
  private buttons: Phaser.GameObjects.Text[] = [];
  private actions: Array<() => void> = [];
  private focused = 0;
  /** Colours still to announce; the popup on show has the keyboard. */
  private popups: CosmeticId[] = [];
  private popup: Phaser.GameObjects.Container | null = null;

  constructor() {
    super('TideSummary');
  }

  init(data: Partial<TideSummaryData>): void {
    this.shown =
      data.summary !== undefined && data.result !== undefined
        ? { summary: data.summary, result: data.result }
        : sampleData();
  }

  create(): void {
    const { summary, result } = this.shown;
    setDevState({ scene: 'TideSummary', outcome: summary.outcome });
    this.buttons = [];
    this.actions = [];
    this.focused = 0;
    this.popup = null;
    this.popups = [...result.newCosmetics];

    drawVerticalGradient(this, [
      [0x0b1a33, 0],
      [0x12395e, 0.6],
      [0x1f78c8, 1],
    ]);

    const summit = summary.outcome === 'summit';
    this.add
      .text(GAME_WIDTH / 2, TITLE_Y, summit ? 'You outran the tide!' : 'The tide got you!', {
        fontFamily: FONT_FAMILY,
        fontSize: IS_PORTRAIT ? '58px' : '54px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    let y = this.drawStats(summary, result.tideStarsTotal);
    y = this.drawClears(summary, y + 18);
    this.drawNews(result, y + 14);
    this.drawButtons(summary);
    this.installInput();

    audioOf(this)?.play(summary.levelsCleared > 0 ? 'star' : 'land');
    this.showNextPopup();
  }

  private drawStats(summary: TideRunSummary, starsTotal: number): number {
    const rows: ReadonlyArray<readonly [string, string]> = [
      ['Levels cleared', `${summary.levelsCleared}`],
      ['Reached', `level ${summary.reachedLevel}`],
      ['Time', formatClock(summary.elapsedMs)],
      ['Speed', `${Math.round(summary.wpm)} wpm`],
      ['Accuracy', `${Math.round(summary.accuracy)}%`],
      // The total that counts toward rewards: the best on each level, like level stars.
      ['Tide stars', `${starsTotal} in all`],
    ];
    const left = IS_PORTRAIT ? 110 : GAME_WIDTH / 2 - 240;
    const right = IS_PORTRAIT ? GAME_WIDTH - 110 : GAME_WIDTH / 2 + 240;
    rows.forEach(([label, value], i) => {
      const y = STATS_TOP + i * STAT_GAP;
      const style = { fontFamily: FONT_FAMILY, fontSize: '26px' };
      this.add.text(left, y, label, { ...style, color: PALETTE.ice }).setOrigin(0, 0.5);
      this.add.text(right, y, value, { ...style, color: PALETTE.snow }).setOrigin(1, 0.5);
    });
    return STATS_TOP + (rows.length - 1) * STAT_GAP;
  }

  /** The last few levels cleared, each with its time and tide stars. */
  private drawClears(summary: TideRunSummary, top: number): number {
    const clears = summary.clears.slice(-CLEARS_SHOWN);
    if (clears.length === 0) return top;
    ensurePlaceholderTexture(this, 'ui/star_full');
    ensurePlaceholderTexture(this, 'ui/star_empty');
    const left = IS_PORTRAIT ? 110 : GAME_WIDTH / 2 - 240;
    const gap = IS_PORTRAIT ? 44 : 32;
    clears.forEach((clear, i) => {
      const y = top + 20 + i * gap;
      const text = `Level ${clear.level}   ${clear.timeSec.toFixed(1)} s${clear.newBest ? '   best!' : ''}`;
      this.add
        .text(left, y, text, {
          fontFamily: FONT_FAMILY,
          fontSize: IS_PORTRAIT ? '24px' : '21px',
          color: clear.newBest ? PALETTE.gold : PALETTE.ice,
        })
        .setOrigin(0, 0.5);
      for (let s = 0; s < 3; s += 1) {
        const x = (IS_PORTRAIT ? GAME_WIDTH - 110 : GAME_WIDTH / 2 + 240) - (2 - s) * 30 - 12;
        this.add
          .image(x, y, s < clear.stars ? 'ui/star_full' : 'ui/star_empty')
          .setDisplaySize(24, 24);
      }
    });
    return top + 20 + (clears.length - 1) * gap + 12;
  }

  /** Records beaten and animals unlocked, in one gold line each. */
  private drawNews(result: TideRunResult, top: number): void {
    const lines = result.newRecords.map((name) => `New record: ${RECORD_WORDS[name]}!`);
    for (const id of result.newUnlocks) {
      const animal = animalById(id);
      if (animal !== undefined) lines.push(`New animal: ${animal.name}!`);
    }
    // Nothing to announce: a kind word instead of an empty space.
    if (lines.length === 0) {
      lines.push(
        this.shown.summary.levelsCleared === 0
          ? 'The water matches your speed, so every run makes you faster.'
          : 'Great climbing! Can you beat it next time?',
      );
    }
    lines.slice(0, 3).forEach((line, i) => {
      this.add
        .text(GAME_WIDTH / 2, top + 18 + i * 34, line, {
          fontFamily: FONT_FAMILY,
          fontSize: '24px',
          color: PALETTE.gold,
        })
        .setOrigin(0.5);
    });
  }

  private drawButtons(summary: TideRunSummary): void {
    const items: ReadonlyArray<readonly [string, () => void]> = [
      ['Try again', () => this.tryAgain(summary.startLevel)],
      ['Back to the map', () => this.scene.start('LevelMap')],
    ];
    const y = GAME_HEIGHT - (IS_PORTRAIT ? 250 : 70);
    items.forEach(([label, action], index) => {
      const button = this.add
        .text(
          IS_PORTRAIT ? GAME_WIDTH / 2 : GAME_WIDTH / 2 + (index - 0.5) * 340,
          IS_PORTRAIT ? y + index * 100 : y,
          label,
          {
            fontFamily: FONT_FAMILY,
            fontSize: '30px',
            color: PALETTE.snow,
            backgroundColor: '#1b2a41',
            padding: { x: 24, y: 12 },
          },
        )
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      if (IS_PORTRAIT) button.setFixedSize(GAME_WIDTH - 120, 0).setAlign('center');
      button.on('pointerover', () => this.focus(index));
      button.on('pointerup', () => {
        if (this.popup === null) pressButton(button, action);
      });
      this.buttons.push(button);
      this.actions.push(action);
    });
    this.focus(0);
  }

  private tryAgain(level: number): void {
    const save = this.registry.get('save') as SaveManager | undefined;
    const history = save?.activeProfile()?.history ?? [];
    this.scene.start('Game', { level, tide: startTideRun(level, history) });
  }

  /* --------------------------------------------------------------- popups */

  /** "New colour: Ocean!" over the summary, one at a time until dismissed. */
  private showNextPopup(): void {
    this.popup?.destroy();
    this.popup = null;
    const id = this.popups.shift();
    if (id === undefined) return;
    const swatch = COLOR_SWATCHES.find((s) => s.cosmetic === id);
    if (swatch === undefined) return;

    const width = Math.min(600, GAME_WIDTH - 40);
    const shade = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x0b1220, 0.6)
      .setOrigin(0)
      .setInteractive();
    const box = this.add.graphics();
    box.fillStyle(0x16233a, 1);
    box.fillRoundedRect(GAME_WIDTH / 2 - width / 2, GAME_HEIGHT / 2 - 170, width, 340, 26);
    box.lineStyle(4, 0xffc145, 1);
    box.strokeRoundedRect(GAME_WIDTH / 2 - width / 2, GAME_HEIGHT / 2 - 170, width, 340, 26);
    box.fillStyle(swatch.value, 1);
    box.fillCircle(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, 42);
    box.lineStyle(4, 0x0b1220, 1);
    box.strokeCircle(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, 42);
    const heading = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 120, `New colour: ${swatch.name}!`, {
        fontFamily: FONT_FAMILY,
        fontSize: '36px',
        color: PALETTE.gold,
      })
      .setOrigin(0.5);
    const note = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 62, 'Try it on the Animals screen.', {
        fontFamily: FONT_FAMILY,
        fontSize: '24px',
        color: PALETTE.ice,
      })
      .setOrigin(0.5);
    const ok = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 122, 'OK', {
        fontFamily: FONT_FAMILY,
        fontSize: '30px',
        color: PALETTE.gold,
        backgroundColor: '#2b4166',
        padding: { x: 40, y: 10 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    ok.on('pointerup', () => pressButton(ok, () => this.showNextPopup()));
    this.popup = this.add.container(0, 0, [shade, box, heading, note, ok]).setDepth(50);
    audioOf(this)?.play('unlock');
  }

  /* ---------------------------------------------------------------- input */

  private focus(index: number): void {
    this.focused = index;
    this.buttons.forEach((button, i) => styleButton(button, i === index));
  }

  private installInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.popup !== null) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
          event.preventDefault();
          this.showNextPopup();
        }
        return;
      }
      const count = this.buttons.length;
      if (event.key === 'Escape') {
        this.scene.start('LevelMap');
      } else if (['ArrowUp', 'ArrowLeft'].includes(event.key)) {
        this.focus((this.focused + count - 1) % count);
      } else if (['ArrowDown', 'ArrowRight'].includes(event.key)) {
        this.focus((this.focused + 1) % count);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const button = this.buttons[this.focused];
        const action = this.actions[this.focused];
        if (button !== undefined && action !== undefined) pressButton(button, action);
      }
    });
  }
}
